import { Container, Table, Button, Alert, Form, Row, Col, Spinner, InputGroup } from "react-bootstrap";
import { useState, useEffect } from "react";
import { getRankingWkaMarzo, getRankingGenerico, getListaEventi } from "../api/api.js";

export default function Rankings() {
  const [rankingData, setRankingData] = useState([]);
  const [eventiList, setEventiList] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState("WKA Marzo");
  const [currentEvento, setCurrentEvento] = useState("");
  const [filterText, setFilterText] = useState(""); // <-- Stato per il filtro di ricerca
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState("it");

  const translations = {
    it: {
      title: "Classifiche & Medagliere",
      subtitle: "Seleziona un evento per visualizzare i piazzamenti ufficiali e il medagliere degli atleti.",
      filter_section: "Seleziona Evento",
      search_placeholder: "Cerca atleta o palestra...", // <-- Nuova traduzione
      select_default: "-- Scegli un evento --",
      gym: "Palestra / Team",
      athlete: "Atleta",
      medals: "Medaglie",
      total: "Totale",
      event_col: "Gara",
      no_data: "Nessun dato registrato per questo specifico evento.",
      loading: "Aggiornamento dati...",
      error_fetch: "Impossibile recuperare i dati del ranking.",
      error_eventi: "Errore nel caricamento dell'elenco eventi.",
      showing_results: "Classifica ufficiale:",
    },
    en: {
      title: "Leaderboards & Medal Tally",
      subtitle: "Select an event to view the official placements and medal counts of the athletes.",
      filter_section: "Select Event",
      search_placeholder: "Search athlete or gym...", // <-- Nuova traduzione
      select_default: "-- Choose an event --",
      gym: "Gym / Team",
      athlete: "Athlete",
      medals: "Medals",
      total: "Total",
      event_col: "Event",
      no_data: "No data available for this specific event.",
      loading: "Updating data...",
      error_fetch: "Error fetching ranking data.",
      error_eventi: "Error loading the events list.",
      showing_results: "Official Standings:",
    },
  };

  const t = (key) => translations[language][key] || key;

  const fetchRanking = async (eventoNome) => {
    if (!eventoNome) return;
    setLoading(true);
    setMessage(null);
    try {
      let res = eventoNome === "WKA Marzo" ? await getRankingWkaMarzo() : await getRankingGenerico(eventoNome);
      if (res.ok) {
        setRankingData(res.data || []);
        setCurrentEvento(eventoNome);
      } else {
        setMessage(res.error || t("error_fetch"));
        setMessageType("danger");
      }
    } catch (err) {
      setMessage(t("error_fetch"));
      setMessageType("danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const inizializzaPagina = async () => {
      try {
        const resEventi = await getListaEventi();
        if (resEventi.ok) setEventiList(resEventi.data || []);
        else {
          setMessage(t("error_eventi"));
          setMessageType("warning");
        }
      } catch (err) {
        console.error("Errore fetch eventi", err);
      }
      fetchRanking("WKA Marzo");
    };
    inizializzaPagina();
  }, []);

  const handleEventoChange = (e) => {
    const valoreSelezionato = e.target.value;
    setSelectedEvento(valoreSelezionato);
    setFilterText(""); // Resetta la ricerca quando cambi evento
    fetchRanking(valoreSelezionato);
  };

  // Filtra l'array mantenendo l'ordinamento del DB
  const filteredData = rankingData.filter((row) => {
    const search = filterText.toLowerCase();
    const palestra = (row.vincitore_p || "").toLowerCase();
    const atleta = (row.vincitore_a || "").toLowerCase();
    return palestra.includes(search) || atleta.includes(search);
  });

  const medalCircleStyle = {
    width: "28px",
    height: "28px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontWeight: "700"
  };

  return (
    <Container className="my-5" style={{ maxWidth: "1000px" }}>
      
      {/* HEADER */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 pb-3 border-bottom">
        <div>
          <h1 className="fw-bold text-dark m-0" style={{ fontSize: "1.8rem" }}>{t("title")}</h1>
          <p className="text-muted m-0 mt-1 small d-none d-md-block">{t("subtitle")}</p>
        </div>
        <Button
          variant="outline-secondary"
          className="mt-3 mt-sm-0 rounded-pill px-3 btn-sm fw-semibold"
          onClick={() => setLanguage(language === "it" ? "en" : "it")}
        >
          {language === "it" ? "🇬🇧 English" : "🇮🇹 Italiano"}
        </Button>
      </div>

      {/* CARD FILTRI (EVENTO + RICERCA TESTUALE) */}
      <div className="p-4 rounded-4 mb-4 shadow-sm bg-white border" style={{ borderColor: "#f0f0f0" }}>
        <Row className="g-3">
          <Col xs={12} md={6}>
            <Form.Group>
              <label className="fw-bold text-uppercase text-secondary mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                {t("filter_section")}
              </label>
              <Form.Select 
                value={selectedEvento} 
                onChange={handleEventoChange}
                disabled={loading}
                className="rounded-3 py-2 px-3 shadow-none"
                style={{ borderColor: "#ced4da", fontSize: "0.95rem" }}
              >
                <option value="">{t("select_default")}</option>
                <option value="WKA Marzo">🏆 WKA Marzo</option>
                {eventiList
                  .filter(ev => ev !== "WKA Marzo")
                  .map((ev, index) => (
                    <option key={index} value={ev}>
                      🏆 {ev}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col xs={12} md={6}>
            <Form.Group>
              <label className="fw-bold text-uppercase text-secondary mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                &nbsp;
              </label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={t("search_placeholder")}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  disabled={loading || rankingData.length === 0}
                  className="rounded-3 py-2 px-3 shadow-none"
                  style={{ fontSize: "0.95rem" }}
                />
                {filterText && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setFilterText("")}
                    style={{ borderTopRightRadius: "8px", borderBottomRightRadius: "8px" }}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ALERT */}
      {message && (
        <Alert variant={messageType} dismissible onClose={() => setMessage(null)} className="mb-4 rounded-3">
          {message}
        </Alert>
      )}

      {/* INFO RISULTATI */}
      {currentEvento && !loading && (
        <div className="d-flex align-items-center mb-3">
          <div className="bg-primary rounded-pill me-2" style={{ width: "4px", height: "18px" }}></div>
          <h5 className="m-0 fw-semibold text-dark">
            {t("showing_results")} <span className="text-primary fw-bold">{currentEvento}</span>
          </h5>
        </div>
      )}

      {/* LOADER */}
      {loading ? (
        <div className="text-center py-5 my-4 bg-white rounded-4 shadow-sm border">
          <Spinner animation="border" variant="primary" size="md" className="mb-2" />
          <p className="text-muted small m-0 fw-medium">{t("loading")}</p>
        </div>
      ) : filteredData.length > 0 ? (
        
        /* TABELLA MEDAGLIERE AGGIORNATA */
        <div className="table-responsive shadow-sm rounded-4 border bg-white">
          <Table hover className="m-0 align-middle" style={{ fontSize: "0.95rem", tableLayout: "fixed", width: "100%", minWidth: "650px" }}>
            <thead className="bg-light" style={{ borderBottom: "2px solid #dee2e6" }}>
              <tr>
                <th className="py-3 px-3 text-secondary text-uppercase fw-bold" style={{ fontSize: "0.75rem", width: "25%" }}>{t("gym")}</th>
                <th className="py-3 px-3 text-secondary text-uppercase fw-bold" style={{ fontSize: "0.75rem", width: "25%" }}>{t("athlete")}</th>
                <th className="py-3 px-3 text-secondary text-uppercase fw-bold text-center" style={{ fontSize: "0.75rem", width: "20%" }}>{t("medals")}</th>
                <th className="py-3 px-3 text-secondary text-uppercase fw-bold text-center" style={{ fontSize: "0.75rem", width: "10%" }}>{t("total")}</th>
                <th className="py-3 px-3 text-secondary text-uppercase fw-bold" style={{ fontSize: "0.75rem", width: "20%" }}>{t("event_col")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, index) => (
                <tr key={index}>
                  <td className="py-3 px-3" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                    <span className="fw-semibold text-dark">{row.vincitore_p || "-"}</span>
                  </td>
                  <td className="py-3 px-3 text-muted" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                    {row.vincitore_a || "-"}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <div className="d-flex justify-content-center gap-2">
                      <span 
                        className="badge bg-warning text-dark rounded-circle" 
                        style={{ ...medalCircleStyle, opacity: row.ori > 0 ? 1 : 0.25 }}
                        title="Oro"
                      >
                        {row.ori}
                      </span>
                      <span 
                        className="badge bg-secondary text-white rounded-circle" 
                        style={{ ...medalCircleStyle, opacity: row.argenti > 0 ? 1 : 0.25 }}
                        title="Argento"
                      >
                        {row.argenti}
                      </span>
                      <span 
                        className="badge text-white rounded-circle" 
                        style={{ ...medalCircleStyle, backgroundColor: "#cd7f32", opacity: row.bronzi > 0 ? 1 : 0.25 }}
                        title="Bronzo"
                      >
                        {row.bronzi}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center fw-bold text-dark">
                    {row.totale}
                  </td>
                  <td className="py-3 px-3 text-secondary" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                    <span className="badge bg-light text-dark border fw-normal py-1 px-2">{row.evento}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        /* FALLBACK */
        !loading && (
          <div className="text-center py-5 bg-white border rounded-4 shadow-sm">
            <span style={{ fontSize: "2rem" }}>ℹ️</span>
            <p className="text-muted mt-2 fw-medium mb-0">{t("no_data")}</p>
          </div>
        )
      )}
    </Container>
  );
}